import { Injectable, inject, signal, computed } from '@angular/core';
import { ConfigService } from '../../shared/services/config.service';

interface CognitoTokens {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

interface AppConfig {
  cognitoDomain: string;
  clientId: string;
  responseType: string;
  scopes: string;
}

interface TokenPayload {
  sub: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  exp: number;
  [key: string]: unknown;
}

const STORAGE_KEY = 'ta_v5_auth';
const TOKEN_REFRESH_LEEWAY_MS = 2 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly config = inject(ConfigService);
  private readonly _sessionVersion = signal(0);
  private refreshPromise: Promise<boolean> | null = null;

  readonly userEmail = computed(() => {
    this._sessionVersion();
    return this.getTokenClaim('email') ?? '';
  });
  readonly userName = computed(() => {
    this._sessionVersion();
    const given = this.getTokenClaim('given_name') ?? '';
    const family = this.getTokenClaim('family_name') ?? '';
    return `${given} ${family}`.trim() || this.userEmail();
  });

  async checkIsSuperAdmin(): Promise<boolean> {
    const apiUrl = this.config.apiUrl;
    const token = await this.getIdTokenForRequest();
    if (!token) return false;

    try {
      const response = await fetch(`${apiUrl}/api/v1/auth/me`, {
        headers: {
          'Authorization': token,
        },
      });
      if (!response.ok) return false;
      const data = await response.json();
      return !!data.isSuperAdmin;
    } catch {
      return false;
    }
  }

  isAuthenticated(): boolean {
    return this.hasValidToken();
  }

  async ensureSession(): Promise<boolean> {
    if (this.hasValidToken()) {
      this.refreshIfNeededInBackground();
      return true;
    }

    if (!this.getRefreshToken()) {
      return false;
    }

    const refreshed = await this.refreshTokens();
    if (!refreshed && !this.hasValidToken()) {
      this.logout();
    }
    return refreshed;
  }

  async getIdTokenForRequest(): Promise<string | null> {
    const idToken = this.getIdToken();
    if (!idToken) {
      return null;
    }

    if (this.hasValidToken()) {
      this.refreshIfNeededInBackground();
      return idToken;
    }

    const refreshed = await this.refreshTokens();
    if (!refreshed) {
      this.logout();
      return null;
    }

    return this.getIdToken();
  }

  async forceRefresh(): Promise<boolean> {
    return this.refreshTokens();
  }

  refreshIfNeededInBackground(): void {
    if (!this.shouldAttemptRefresh()) {
      return;
    }
    void this.refreshTokens();
  }

  async login(): Promise<void> {
    const appConfig = await this.fetchAppConfig();

    // Support ephemeral native login redirect
    if ((appConfig as any).isEphemeral) {
      window.location.href = '/auth/ephemeral-login';
      return;
    }

    const redirectUri = `${window.location.origin}/auth/callback`;

    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.deriveCodeChallenge(codeVerifier);
    sessionStorage.setItem('ta_v5_pkce_verifier', codeVerifier);

    const params = new URLSearchParams({
      response_type: appConfig.responseType,
      client_id: appConfig.clientId,
      redirect_uri: redirectUri,
      scope: appConfig.scopes,
      identity_provider: 'Google',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${appConfig.cognitoDomain}/oauth2/authorize?${params.toString()}`;
  }

  async handleCallback(code: string): Promise<void> {
    const redirectUri = `${window.location.origin}/auth/callback`;
    const apiUrl = this.config.apiUrl;

    const codeVerifier = sessionStorage.getItem('ta_v5_pkce_verifier');
    if (!codeVerifier) {
      throw new Error('PKCE verifier not found — please restart login');
    }
    sessionStorage.removeItem('ta_v5_pkce_verifier');

    const response = await fetch(`${apiUrl}/api/v1/auth/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirectUri, codeVerifier }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error ?? 'Token exchange failed');
    }

    const tokens: CognitoTokens = await response.json();
    this.storeTokens(tokens);
  }

  logout(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.markSessionChanged();
  }

  getIdToken(): string | null {
    return this.getStoredTokens()?.id_token ?? null;
  }

  getAccessToken(): string | null {
    return this.getStoredTokens()?.access_token ?? null;
  }

  private async fetchAppConfig(): Promise<AppConfig> {
    const apiUrl = this.config.apiUrl;
    const response = await fetch(`${apiUrl}/api/v1/appconfig`);
    if (!response.ok) {
      throw new Error('Failed to fetch app config');
    }
    return response.json();
  }

  private storeTokens(tokens: CognitoTokens): void {
    const existing = this.getStoredTokens();
    const nextTokens: CognitoTokens = {
      ...tokens,
      refresh_token: tokens.refresh_token ?? existing?.refresh_token,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextTokens));
    this.markSessionChanged();
  }

  private getStoredTokens(): CognitoTokens | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      this.markSessionChanged();
      return null;
    }
  }

  private getRefreshToken(): string | null {
    return this.getStoredTokens()?.refresh_token ?? null;
  }

  private shouldAttemptRefresh(): boolean {
    if (!this.getRefreshToken()) {
      return false;
    }

    const expiresAt = this.getTokenExpiryMs();
    if (!expiresAt) {
      return true;
    }

    return expiresAt <= Date.now() + TOKEN_REFRESH_LEEWAY_MS;
  }

  private async refreshTokens(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    this.refreshPromise = this.performRefresh(refreshToken).finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  private async performRefresh(refreshToken: string): Promise<boolean> {
    try {
      const apiUrl = this.config.apiUrl;
      const response = await fetch(`${apiUrl}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const refreshedTokens = await response.json().catch(() => null) as Partial<CognitoTokens> | null;
      if (!refreshedTokens || !this.isValidTokenResponse(refreshedTokens)) {
        return false;
      }

      this.storeTokens({
        id_token: refreshedTokens.id_token,
        access_token: refreshedTokens.access_token,
        expires_in: refreshedTokens.expires_in,
        token_type: refreshedTokens.token_type,
        refresh_token: refreshedTokens.refresh_token ?? refreshToken,
      });

      return true;
    } catch {
      return false;
    }
  }

  private isValidTokenResponse(tokens: Partial<CognitoTokens>): tokens is CognitoTokens {
    return typeof tokens.id_token === 'string'
      && typeof tokens.access_token === 'string'
      && typeof tokens.expires_in === 'number'
      && typeof tokens.token_type === 'string';
  }

  private decodeIdToken(): TokenPayload | null {
    const idToken = this.getIdToken();
    if (!idToken) return null;

    return this.decodeJwtPayload(idToken);
  }

  private decodeJwtPayload(token: string): TokenPayload | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;

      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }

  private getTokenClaim(claim: string): string | undefined {
    const payload = this.decodeIdToken();
    if (!payload) return undefined;
    const value = payload[claim];
    return typeof value === 'string' ? value : undefined;
  }

  private hasValidToken(): boolean {
    const expiresAt = this.getTokenExpiryMs();
    return expiresAt !== null && expiresAt > Date.now();
  }

  private getTokenExpiryMs(): number | null {
    const payload = this.decodeIdToken();
    if (!payload) return null;
    return payload.exp * 1000;
  }

  private markSessionChanged(): void {
    this._sessionVersion.update((value) => value + 1);
  }

  private generateCodeVerifier(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return this.base64UrlEncode(array);
  }

  private async deriveCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(digest));
  }

  private base64UrlEncode(buffer: Uint8Array): string {
    let binary = '';
    for (const byte of buffer) {
      binary += String.fromCharCode(byte);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
